import React, { useRef } from 'react';
import classnames from 'classnames';
import { Container } from 'src/components/Layout';
import { LazyImg } from 'src/hooks/useLazyLoad';
import { useMarqueeLoopPx } from 'src/hooks/useMarqueeLoopPx';
import { TrustPilot } from 'src/components/TrustPilot/TrustPilot';
import { useCookieString, isIrishUserCookieValue } from 'src/util/cookies';

import classes from './Usps.scss';

type Props = Record<string, unknown>;

type UspItem = {
    title: string;
    desc?: string;
    icon?: string;
    link?: string;
};

export const Usps: React.FC<Props> = () => {
    const cookieValue = useCookieString('isIrishUser');

    const data: UspItem[] = [
        {
            title: 'Free Returns',
            desc: 'For Up To 28 Days!*',
            icon: require(`../../../static/img/usp_delivery.svg`),
            link: '/free-returns',
        },
        {
            title: 'Price Beat Guarantee',
            desc: 'Loved By Over 2 million customers!',
            icon: require(`../../../static/img/usp_promise.svg`),
        },
        {
            title: 'Free Logo Setup',
            desc: 'We’ll create your logo for free',
            icon: require(`../../../static/img/usp_free.svg`),
        },
        {
            title: 'Reviews',
        },
    ];

    // A missing cookie renders as UK: the SSR cache key does too, so a cached page hydrates for both.
    const isIrishUser = isIrishUserCookieValue(cookieValue);

    if (isIrishUser) {
        data[0].title = 'Easy Returns';
    }

    const marqueeLayoutKey = `${cookieValue ?? ''}-${isIrishUser}-${data.map((d) => d.title).join('|')}`;
    const marqueeTrackRef = useRef<HTMLUListElement>(null);
    const marqueeLoopPx = useMarqueeLoopPx(marqueeTrackRef, data.length, marqueeLayoutKey);

    const renderUspItem = (item: UspItem, index: number, keyPrefix: string, showTrustPilot: boolean) => {
        if (item.link) {
            return (
                <li key={`${keyPrefix}-${index}`} className={classes.uspItem}>
                    <a href={item.link} className={classes.uspItemLink}>
                        <figure className={classes.uspItemIcon}>
                            <LazyImg src={item.icon!} alt={item.title} width={16} height={16} />
                        </figure>
                        <span>
                            <b className={classes.uspItemTitle}>{item.title}</b> {item.desc}
                        </span>
                    </a>
                </li>
            );
        }

        return (
            <li key={`${keyPrefix}-${index}`} className={classes.uspItem}>
                {item.icon && (
                    <>
                        <figure className={classes.uspItemIcon}>
                            <LazyImg src={item.icon!} alt={item.title} width={16} height={16} />
                        </figure>
                        <span>
                            <b className={classes.uspItemTitle}>{item.title}</b> {item.desc}
                        </span>
                    </>
                )}
                {item.title === 'Reviews' && showTrustPilot && <TrustPilot theme="dark" />}
            </li>
        );
    };

    return (
        <Container className={classes.uspContainer}>
            <div className={classes.uspDesktop}>
                <ul className={classes.uspList}>
                    {/* TrustPilot only in the mobile marquee; CSS picks the list, so hydration can't change it. */}
                    {data.map((item, index) => renderUspItem(item, index, 'desktop', false))}
                </ul>
            </div>

            <div className={classes.uspMobileMarquee}>
                <ul
                    ref={marqueeTrackRef}
                    className={classnames(
                        classes.uspMarqueeTrack,
                        marqueeLoopPx != null && classes.uspMarqueeTrackAnimating,
                    )}
                    style={
                        marqueeLoopPx != null
                            ? ({ '--usp-marquee-loop': `${marqueeLoopPx}px` } as React.CSSProperties)
                            : undefined
                    }
                >
                    {[...data, ...data].map((item, index) =>
                        renderUspItem(item, index, 'marquee', index < data.length),
                    )}
                </ul>
            </div>
        </Container>
    );
};
